import { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useTranslation } from 'react-i18next';
import { changelog } from '../data/changelog';
import { AppDialogTitle } from './common/AppDialogTitle';

const STORAGE_KEY = 'pm_changelog_last_seen';

export function ChangelogDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const latestVersion = changelog.length > 0 ? changelog[0]!.version : '';

  const calcUnread = useCallback(() => {
    try {
      const lastSeen = localStorage.getItem(STORAGE_KEY);
      if (!lastSeen) return changelog.length;
      const idx = changelog.findIndex((v) => v.version === lastSeen);
      return idx < 0 ? changelog.length : idx;
    } catch {
      return changelog.length;
    }
  }, []);

  useEffect(() => {
    setUnreadCount(calcUnread());
  }, [calcUnread]);

  const handleOpen = () => {
    setOpen(true);
    try {
      localStorage.setItem(STORAGE_KEY, latestVersion);
    } catch { /* noop */ }
    setUnreadCount(0);
  };

  return (
    <>
      <Tooltip title={t('changelog.title')}>
        <IconButton onClick={handleOpen} size="small">
          <Badge badgeContent={unreadCount} color="error" max={9}>
            <NewReleasesIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewReleasesIcon color="primary" />
          {t('changelog.title')}
        </AppDialogTitle>
        <DialogContent dividers>
          {changelog.map((entry, vi) => {
            // Defensiv: Einträge müssen nicht alle drei Rubriken tragen (z. B. reine
            // Verbesserungs-Releases ohne features/fixes) — fehlende Arrays sonst -> Crash.
            const features = entry.features ?? [];
            const improvements = entry.improvements ?? [];
            const fixes = entry.fixes ?? [];
            return (
            <Box key={entry.version} sx={{ mb: vi < changelog.length - 1 ? 4 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Chip
                  label={entry.version}
                  color={vi === 0 ? 'primary' : 'default'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {entry.date}
                </Typography>
              </Box>

              {features.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <StarIcon fontSize="small" color="warning" />
                    {t('changelog.features')}
                  </Typography>
                  <List dense disablePadding>
                    {features.map((f, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.25, pl: 1 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {improvements.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <BuildIcon fontSize="small" color="info" />
                    {t('changelog.improvements')}
                  </Typography>
                  <List dense disablePadding>
                    {improvements.map((imp, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.25, pl: 1 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'info.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={imp} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {fixes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <BugReportIcon fontSize="small" color="error" />
                    {t('changelog.fixes')}
                  </Typography>
                  <List dense disablePadding>
                    {fixes.map((fix, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.25, pl: 1 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={fix} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
